<script setup lang="ts">
import type { ToastHtmlElement } from "@/types"
import { useMutation } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, ref, useTemplateRef } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { unlockAlbum } from "@/api"
import { Button, Icon, Input, Toast } from "@/components/ui"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { GuestLayout } from "@/layouts"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/album/[albumId]/unlock", value => Number(value))
const albumId = useConvertedRouteParam("albumId", "/guest/[id]/album/[albumId]/unlock", value => Number(value))

const { t } = useTranslation()
const router = useRouter()

const validationErrors = ref()
const errorToastRef = useTemplateRef<ToastHtmlElement | null>("errorToastRef")

const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      passcode: z.string().min(1, t("unlock_album_page.error.passcode")),
    }),
  ),
)

const { handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    passcode: "",
  },
})

const { mutate: accessAlbumMutation, isPending } = useMutation({
  mutationFn: (passcode: string) => unlockAlbum(passcode, eventVaultId.value, albumId.value),
  onSuccess: () => {
    router.push(`/guest/gallery/${eventVaultId.value}?album=${albumId.value}`)
  },
  onError: (error) => {
    let errorMessage = t("check_details_try_again")

    if (error instanceof ValidationException) {
      errorMessage = t("validation_error")
      validationErrors.value = error
    }

    errorToastRef.value?.showToast({ content: errorMessage, title: t("something_went_wrong"), icon: "Caution" })
  },
})

const onSubmit = handleSubmit(({ passcode }) => {
  validationErrors.value = undefined
  accessAlbumMutation(passcode)
})
</script>

<template>
  <GuestLayout>
    <div class="container gpx-unlock-album-page-container">
      <Icon class="gpx-unlock-album-page-icon" icon="FolderLock" size="4xl" />
      <form class="gpx-unlock-album-page-form" @submit.prevent="onSubmit">
        <h1 class="heading-title">
          {{ t("unlock_album_page.title") }}
        </h1>
        <p>{{ t("unlock_album_page.message") }}</p>
        <Input :disabled="isPending" name="passcode" :label="t('unlock_album_page.form.passcode')" />
        <Button :disabled="isPending" size="lg">
          {{ t("submit") }}
        </Button>
      </form>
    </div>

    <Toast ref="errorToastRef" variant="danger" />
  </GuestLayout>
</template>

<style scoped>
.gpx-unlock-album-page-container {
  margin: 3rem auto;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.gpx-unlock-album-page-form {
  max-width: 25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gpx-unlock-album-page-icon {
  fill: var(--template-primary-colour);
  color: var(--template-primary-colour);
}

@media (width >= 768px) {
  .gpx-unlock-album-page-container {
    flex-direction: row;
    align-items: start;
    text-align: left;
    gap: 2rem;
  }

  .gpx-unlock-album-page-form button {
    width: 10rem;
  }
}
</style>
